// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Event-scaled camera shake (M3-11).
//
// A pop bumper firing under a steel ball is a bang you feel in the cabinet. On a screen there
// is nothing to feel it with, so the convention is to move the camera a little — and the whole
// craft of it is in *how* little. A pinball impact is a sharp knock and a fast settle, not a
// wobble: too much amplitude and the machine reads as made of rubber, too long a decay and the
// picture is never still, and either one makes the ball harder to track, which is the one thing
// a pinball camera must never do.
//
// Three decisions carry it:
//
//   * **Scaled by the event, not by the event's name.** `SimEvent.value` is an impact speed the
//     solver already computed. A bumper the ball dribbles into and a bumper it is fired into at
//     four metres a second are the same event with very different numbers, and a shake that
//     ignored the number would be a shake that lies about what happened.
//   * **Energy, not a queue of shakes.** Events add into one decaying scalar, so a bumper storm
//     builds instead of restarting, and the sum is clamped so it cannot run away.
//   * **Step index, not a clock.** The oscillator's phase is the simulation's own step counter,
//     the same discipline as `mechanismTiming.ts` and `lampStates.ts`. A frame drawn between two
//     steps interpolates; nothing here integrates, so a slow device sees a slower-sampled shake
//     rather than a different one.
//
// Not applied to the 2-D collision view, and that is deliberate: it is a diagnostic instrument,
// and an instrument that moves when the thing it is measuring does cannot be read.

import { STEP_HZ } from '@core/sim/constants'
import {
  EV_BALL_CAPTURED,
  EV_BALL_EJECTED,
  EV_BANK_COMPLETE,
  EV_BUMPER_HIT,
  EV_SLINGSHOT_FIRED,
  EV_TARGET_DOWN,
} from '@core/sim/events'

/**
 * How much energy each kind of event contributes at full strength.
 *
 * An event absent from this table shakes nothing. Most of them should: a switch closing, a
 * spinner turning and a gate swinging are things the machine does quietly, and a camera that
 * jumped at a spinner would never be still during a ramp shot.
 */
export const SHAKE_GAINS: Readonly<Record<number, number>> = {
  // The loudest mechanism on the playfield, and the one a player expects to feel.
  [EV_BUMPER_HIT]: 0.34,
  // A slingshot is as violent as a bumper but it fires beside the flippers, where the player
  // is looking hardest. Slightly less, or the shot they are lining up moves under them.
  [EV_SLINGSHOT_FIRED]: 0.26,
  // A coil throwing a ball out of a scoop: a solid thump with the ball's exit speed behind it.
  [EV_BALL_EJECTED]: 0.24,
  // A ball dropping into a lock. Softer — the machine absorbs it rather than kicking.
  [EV_BALL_CAPTURED]: 0.14,
  [EV_TARGET_DOWN]: 0.12,
  // Not an impact at all but an achievement, which is the other reason to shake a camera.
  // Deliberately the largest single contribution in the table.
  [EV_BANK_COMPLETE]: 0.5,
}

/**
 * The speed, m/s, at which an impact counts as full strength.
 *
 * A ball off a hard flipper shot is about six; four is a firm, ordinary hit. Above it the
 * contribution saturates rather than continuing to climb, because the difference between a
 * hard hit and a very hard hit is not something a camera can express.
 */
export const SHAKE_REFERENCE_SPEED = 4

/** Steps for the energy to fall to 1/e. About a tenth of a second: a knock, not a wobble. */
export const SHAKE_DECAY_STEPS = 105

/** Below this the shake is over. Stops the camera jittering by micrometres for ever. */
const SHAKE_FLOOR = 1e-4

/** Peak translation at full energy, metres, in the camera's own right/up plane. */
export const SHAKE_MAX_TRANSLATION = 0.0062

/** Peak roll at full energy, radians. About a third of a degree. */
export const SHAKE_MAX_ROLL = 0.0065

/**
 * Oscillator frequencies, Hz.
 *
 * Two per axis at incommensurate ratios, so the sum never repeats over the life of a shake and
 * the motion does not read as a sine wave. Around 15–25 Hz: fast enough to be a knock, slow
 * enough to survive being sampled at sixty frames a second without aliasing into a slow drift.
 */
const FREQUENCIES = {
  x: [23.1, 16.7],
  y: [19.3, 13.1],
  roll: [14.9, 21.7],
} as const

/** Phase offsets, radians, so the three axes do not all peak on the same beat. */
const PHASES = { x: [0, 1.91], y: [0.73, 2.64], roll: [1.27, 0.34] } as const

/** Where the camera should sit this frame, relative to its pose. Reused; never allocated. */
export interface ShakeOffset {
  /** Camera-local right, metres. */
  x: number
  /** Camera-local up, metres. */
  y: number
  /** Roll about the view axis, radians. */
  roll: number
}

export function createShakeOffset(): ShakeOffset {
  return { x: 0, y: 0, roll: 0 }
}

function wave(t: number, freqs: readonly [number, number], phases: readonly [number, number]): number {
  const a = Math.sin(t * ((Math.PI * 2 * freqs[0]) / STEP_HZ) + phases[0])
  const b = Math.sin(t * ((Math.PI * 2 * freqs[1]) / STEP_HZ) + phases[1])
  return a * 0.62 + b * 0.38
}

export class CameraShake {
  /** 0 to 1. One scalar for the whole machine, because the camera only has one. */
  private energy = 0
  private lastStep = -1

  /**
   * Multiplies everything, and is zero under `prefers-reduced-motion`.
   *
   * Held rather than passed so a caller cannot apply it at one call site and forget the other;
   * `MotionPreference.scale` is what belongs here.
   */
  scale = 1

  get level(): number {
    return this.energy
  }

  /**
   * Fold one simulation event into the shake.
   *
   * `step` is the event's own step, which may be a dozen steps behind the frame being drawn.
   * Decaying to it first is what makes a burst of events spread across a frame land with the
   * weights they actually had, rather than all arriving together at the frame boundary.
   */
  add(type: number, value: number, step: number): void {
    const gain = SHAKE_GAINS[type]
    if (gain === undefined || this.scale <= 0) return
    this.advanceTo(step)
    const strength = Math.min(Math.abs(value) / SHAKE_REFERENCE_SPEED, 1)
    this.energy = Math.min(this.energy + gain * strength, 1)
  }

  /**
   * Decay the energy forward to a step.
   *
   * A step *behind* the accumulator is a new machine — the harness rebuilds on `R`, which
   * replaces the world and restarts the counter — so the shake starts over rather than
   * decaying from a number that belongs to a game nobody is playing.
   */
  advanceTo(step: number): void {
    if (this.lastStep < 0 || step < this.lastStep) {
      this.lastStep = step
      return
    }
    const elapsed = step - this.lastStep
    if (elapsed === 0) return
    this.lastStep = step
    if (this.energy <= 0) return
    this.energy *= Math.exp(-elapsed / SHAKE_DECAY_STEPS)
    if (this.energy < SHAKE_FLOOR) this.energy = 0
  }

  /**
   * Where the camera goes this frame.
   *
   * `alpha` is the driver's interpolation fraction, so the oscillator is sampled at the same
   * sub-step position the rest of the picture is drawn at. Without it the shake would step in
   * 1 ms jumps while everything around it moved smoothly, which reads as the camera stuttering
   * rather than as the machine being hit.
   */
  sample(step: number, alpha: number, out: ShakeOffset): ShakeOffset {
    this.advanceTo(step)
    const amplitude = this.energy * this.scale
    if (amplitude <= 0) {
      out.x = 0
      out.y = 0
      out.roll = 0
      return out
    }
    const t = step + alpha
    // Squared, so a small knock is much smaller than a big one rather than merely smaller.
    // Linear energy makes every event feel about the same, which is the failure mode that
    // makes shake read as a screen effect instead of as the machine.
    const gain = amplitude * amplitude
    out.x = wave(t, FREQUENCIES.x, PHASES.x) * SHAKE_MAX_TRANSLATION * gain
    out.y = wave(t, FREQUENCIES.y, PHASES.y) * SHAKE_MAX_TRANSLATION * gain
    out.roll = wave(t, FREQUENCIES.roll, PHASES.roll) * SHAKE_MAX_ROLL * gain
    return out
  }

  /** Forget everything. The machine was rebuilt, or the player paused. */
  reset(): void {
    this.energy = 0
    this.lastStep = -1
  }
}
